import ProxyAddressesGrid from "@cayo/admin-components/components/grids/ProxyAddressesGrid";
import SimpleGrid, { ISimpleGrid } from "@cayo/admin-components/components/grids/SimpleGrid";
import { JoinToGroupsGrid } from "@cayo/core/admin-components/grids/JoinToGroupsGrid";
import SpnGrid, { ISpnGrid } from "@cayo/core/admin-components/grids/SpnGrid";
import { IProxyAddressesGrid, IObjectSelectionGrid } from "@cayo/shared/api/types/schema";
import { IAdditionalFormFieldProps } from "@cayo/shared/components/Form/types";
import BitLockerPasswords from "./BitLockerPasswords";
import ComputersGrid from "./ComputersGrid";
import ObjectSelectionGrid from "./ObjectSelectionGrid";
import { IMembersLookupData } from "./ObjectSelectionGrid/types";

const objectselectiongrid = (
  annotation: IObjectSelectionGrid,
  addProps: IAdditionalFormFieldProps
): JSX.Element => {
  const { name, globalSearchDescriptor, allowRemove, readOnly, columns, ...rest } = annotation;
  const { value, setValue } = addProps;
  const membersLookupData: IMembersLookupData =
    addProps.object?.MembersData_Lookup || addProps.object?.MemberOfData_Lookup;

  return (
    <ObjectSelectionGrid
      {...rest}
      key={name || annotation.label}
      name={name}
      membersLookupData={membersLookupData}
      globalSearchDescriptor={globalSearchDescriptor}
      allowRemove={allowRemove}
      readOnly={readOnly}
      columns={columns}
      value={value}
      setValue={(v) => setValue(v, annotation)}
    />
  );
};

const proxyaddressesgrid = (
  annotation: IProxyAddressesGrid,
  addProps: IAdditionalFormFieldProps
): JSX.Element => {
  const { value, setValue } = addProps;
  const { name, label, allowCustomDomainInput, columns, domains } = annotation;

  return (
    <ProxyAddressesGrid
      key={name || label}
      name={name}
      allowCustomDomainInput={allowCustomDomainInput}
      {...annotation}
      domains={domains}
      columns={columns}
      value={value}
      setValue={(v) => setValue(v, annotation)}
    />
  );
};

const computersgrid = (
  annotation: IObjectSelectionGrid,
  addProps: IAdditionalFormFieldProps
): JSX.Element => {
  const { value, setValue } = addProps;

  return (
    <ComputersGrid
      key={annotation.name || annotation.label}
      name={annotation.name}
      {...annotation}
      columns={annotation.columns}
      value={value}
      setValue={(v) => setValue(v, annotation)}
    />
  );
};

const simplegrid = (annotation: ISimpleGrid, addProps: IAdditionalFormFieldProps) => {
  const { name, label, hidden, dataTestId, readOnly, disabled, noItemsText } = annotation;
  const { value } = addProps;

  const key = name || label;

  return (
    <SimpleGrid
      key={key}
      readOnly={readOnly}
      disabled={disabled}
      name={name!}
      label={label}
      items={value}
      hidden={hidden}
      dataTestId={dataTestId}
      noItemsText={noItemsText}
    />
  );
};

const spngrid = (annotation: ISpnGrid, addProps: IAdditionalFormFieldProps) => {
  const { value, setValue, object } = addProps;
  const {
    name,
    label,
    readOnly,
    disabled,
    noItemsText,
    layoutMode,
    columns,
    selectionMode,
    hidden,
    dataTestId,
  } = annotation;

  const expandedFieldName = `${annotation.name}_Expanded`;
  const expanded = object[expandedFieldName];

  const collapsed =
    value && !Array.isArray(value) && "newSpnList" in value ? value.newSpnList : value;

  const key = name || label;

  return (
    <SpnGrid
      key={key}
      readOnly={readOnly}
      disabled={disabled}
      name={name!}
      label={label}
      items={{ collapsed, expanded }}
      hidden={hidden}
      dataTestId={dataTestId}
      noItemsText={noItemsText}
      columns={columns}
      layoutMode={layoutMode}
      selectionMode={selectionMode}
      setValue={(v) => {
        setValue({ newSpnList: v }, { ...annotation, type: "" });
        setValue(v, { ...annotation, name: expandedFieldName, type: "" });
      }}
    />
  );
};

const jointogroupsgrid = (
  annotation: IObjectSelectionGrid & { dataTestId: string },
  addProps: IAdditionalFormFieldProps
) => {
  const { value, setValue } = addProps;
  const { name, label, noItemsText, columns, dataTestId } = annotation;

  const key = name || label;

  return (
    <JoinToGroupsGrid
      key={key}
      name={name!}
      label={label}
      dataTestId={dataTestId}
      noItemsText={noItemsText}
      columns={columns}
      value={value}
      setValue={(payload) => setValue(payload, annotation)}
    />
  );
};

const bitlockerpasswords = (annotation: ISimpleGrid, addProps: IAdditionalFormFieldProps) => {
  const { name, label, hidden, dataTestId, readOnly, disabled, noItemsText } = annotation;
  const { value: bitLockerPasswords, intl, theme, getIconAnnotation } = addProps;

  const key = name || label;

  const items =
    (bitLockerPasswords && "value" in bitLockerPasswords
      ? bitLockerPasswords.value
      : bitLockerPasswords) || [];

  return (
    <BitLockerPasswords
      key={key}
      readOnly={readOnly}
      disabled={disabled}
      name={name!}
      label={label}
      items={items}
      hidden={hidden}
      dataTestId={dataTestId}
      noItemsText={noItemsText}
      intl={intl}
      theme={theme}
      getIconAnnotation={getIconAnnotation}
    />
  );
};

const adminPickerRenderers = {
  objectselectiongrid, // TODO: include admin's renderers conditionaly
  proxyaddressesgrid,
  computersgrid,
  simplegrid,
  bitlockerpasswords,
  spngrid,
  jointogroupsgrid,
};

export default adminPickerRenderers;
